/**
 * Centralized Timezone Configuration
 * Single source of truth for all timezone dropdowns across the app
 * 
 * IMPORTANT: All timezone dropdowns should import from this file
 * to ensure consistency across the application.
 */

// Comprehensive timezone list organized by region
export const TIMEZONES = [
  // Gulf / Middle East
  { value: 'Asia/Dubai', label: 'GST (Gulf) - Dubai/UAE', abbr: 'GST', region: 'middle_east' },
  { value: 'Asia/Abu_Dhabi', label: 'GST (Gulf) - Abu Dhabi', abbr: 'GST', region: 'middle_east' },
  { value: 'Asia/Muscat', label: 'GST (Gulf) - Muscat/Oman', abbr: 'GST', region: 'middle_east' },
  { value: 'Asia/Qatar', label: 'AST - Qatar', abbr: 'AST', region: 'middle_east' },
  { value: 'Asia/Kuwait', label: 'AST - Kuwait', abbr: 'AST', region: 'middle_east' },
  { value: 'Asia/Bahrain', label: 'AST - Bahrain', abbr: 'AST', region: 'middle_east' },
  { value: 'Asia/Riyadh', label: 'AST - Saudi Arabia', abbr: 'AST', region: 'middle_east' },
  
  // Asia - Major Cities
  { value: 'Asia/Singapore', label: 'SGT - Singapore', abbr: 'SGT', region: 'asia' },
  { value: 'Asia/Hong_Kong', label: 'HKT - Hong Kong', abbr: 'HKT', region: 'asia' },
  { value: 'Asia/Tokyo', label: 'JST - Tokyo/Japan', abbr: 'JST', region: 'asia' },
  { value: 'Asia/Seoul', label: 'KST - Seoul/Korea', abbr: 'KST', region: 'asia' },
  { value: 'Asia/Shanghai', label: 'CST - China', abbr: 'CST', region: 'asia' },
  { value: 'Asia/Kolkata', label: 'IST - India', abbr: 'IST', region: 'asia' },
  { value: 'Asia/Bangkok', label: 'ICT - Bangkok/Thailand', abbr: 'ICT', region: 'asia' },
  { value: 'Asia/Jakarta', label: 'WIB - Jakarta/Indonesia', abbr: 'WIB', region: 'asia' },
  { value: 'Asia/Manila', label: 'PHT - Manila/Philippines', abbr: 'PHT', region: 'asia' },
  { value: 'Asia/Kuala_Lumpur', label: 'MYT - Malaysia', abbr: 'MYT', region: 'asia' },
  
  // United States
  { value: 'America/New_York', label: 'EST (Eastern)', abbr: 'EST', region: 'usa' },
  { value: 'America/Chicago', label: 'CST (Central)', abbr: 'CST', region: 'usa' },
  { value: 'America/Denver', label: 'MST (Mountain)', abbr: 'MST', region: 'usa' },
  { value: 'America/Phoenix', label: 'MST (Arizona)', abbr: 'MST', region: 'usa' },
  { value: 'America/Los_Angeles', label: 'PST (Pacific)', abbr: 'PST', region: 'usa' },
  { value: 'America/Anchorage', label: 'AKST (Alaska)', abbr: 'AKST', region: 'usa' },
  { value: 'Pacific/Honolulu', label: 'HST (Hawaii)', abbr: 'HST', region: 'usa' },
  
  // Europe
  { value: 'Europe/London', label: 'GMT - London/UK', abbr: 'GMT', region: 'europe' },
  { value: 'Europe/Paris', label: 'CET - Paris/France', abbr: 'CET', region: 'europe' },
  { value: 'Europe/Berlin', label: 'CET - Berlin/Germany', abbr: 'CET', region: 'europe' },
  { value: 'Europe/Amsterdam', label: 'CET - Amsterdam', abbr: 'CET', region: 'europe' },
  { value: 'Europe/Madrid', label: 'CET - Madrid/Spain', abbr: 'CET', region: 'europe' },
  { value: 'Europe/Rome', label: 'CET - Rome/Italy', abbr: 'CET', region: 'europe' },
  { value: 'Europe/Zurich', label: 'CET - Zurich/Switzerland', abbr: 'CET', region: 'europe' },
  { value: 'Europe/Moscow', label: 'MSK - Moscow/Russia', abbr: 'MSK', region: 'europe' },
  
  // Australia / Pacific
  { value: 'Australia/Sydney', label: 'AEST - Sydney', abbr: 'AEST', region: 'oceania' },
  { value: 'Australia/Melbourne', label: 'AEST - Melbourne', abbr: 'AEST', region: 'oceania' },
  { value: 'Australia/Brisbane', label: 'AEST - Brisbane', abbr: 'AEST', region: 'oceania' },
  { value: 'Australia/Perth', label: 'AWST - Perth', abbr: 'AWST', region: 'oceania' },
  { value: 'Pacific/Auckland', label: 'NZST - Auckland/NZ', abbr: 'NZST', region: 'oceania' },
  
  // Americas (Non-US)
  { value: 'America/Toronto', label: 'EST - Toronto/Canada', abbr: 'EST', region: 'americas' },
  { value: 'America/Vancouver', label: 'PST - Vancouver/Canada', abbr: 'PST', region: 'americas' },
  { value: 'America/Mexico_City', label: 'CST - Mexico City', abbr: 'CST', region: 'americas' },
  { value: 'America/Sao_Paulo', label: 'BRT - São Paulo/Brazil', abbr: 'BRT', region: 'americas' },
  { value: 'Atlantic/Canary', label: 'WET - Canary Islands', abbr: 'WET', region: 'americas' }
];

// Grouped timezones for dropdown menus with sections
export const TIMEZONES_BY_REGION = {
  'Gulf / Middle East': TIMEZONES.filter(tz => tz.region === 'middle_east'),
  'Asia': TIMEZONES.filter(tz => tz.region === 'asia'),
  'United States': TIMEZONES.filter(tz => tz.region === 'usa'),
  'Europe': TIMEZONES.filter(tz => tz.region === 'europe'),
  'Australia / Pacific': TIMEZONES.filter(tz => tz.region === 'oceania'),
  'Americas': TIMEZONES.filter(tz => tz.region === 'americas')
};

// Country-specific timezone subsets
export const COUNTRY_TIMEZONES = {
  UAE: TIMEZONES.filter(tz => ['Asia/Dubai', 'Asia/Abu_Dhabi'].includes(tz.value)),
  USA: TIMEZONES.filter(tz => tz.region === 'usa'),
  // Add more countries as needed
};

/**
 * Get timezone abbreviation from IANA timezone value
 * @param {string} timezone - IANA timezone (e.g., 'Asia/Dubai')
 * @returns {string} Abbreviation (e.g., 'GST')
 */
export const getTimezoneAbbr = (timezone) => {
  const tz = TIMEZONES.find(t => t.value === timezone);
  return tz?.abbr || 'UTC';
};

/**
 * Get timezone label from IANA timezone value
 * @param {string} timezone - IANA timezone (e.g., 'Asia/Dubai')
 * @returns {string} Label (e.g., 'GST (Gulf) - Dubai/UAE')
 */
export const getTimezoneLabel = (timezone) => {
  const tz = TIMEZONES.find(t => t.value === timezone);
  return tz?.label || timezone;
};

/**
 * Get timezones for a specific country
 * @param {string} countryCode - Country code (e.g., 'UAE', 'USA')
 * @returns {Array} List of timezone objects for the country
 */
export const getTimezonesForCountry = (countryCode) => {
  return COUNTRY_TIMEZONES[countryCode] || TIMEZONES;
};

/**
 * Default timezone (Gulf/Dubai for primary market)
 */
export const DEFAULT_TIMEZONE = 'Asia/Dubai';

/**
 * Default timezone for USA
 */
export const DEFAULT_TIMEZONE_USA = 'America/Chicago';
